/**
 * PDP custom elements: <media-gallery>, <variant-picker>, <quantity-input>,
 * <product-form>. Replaces the live theme's jQuery stack (Swiper galleries,
 * select2 selects, theme.ProductPage variant callback, Ajaxify add-to-cart).
 *
 * Contracts kept from the live theme:
 * - `.money` spans wrap every price (currency-converter / app hook).
 * - The hidden `<select name="id">` master select keeps `data-sku` and
 *   `data-inventory_*` attributes (stock status source, LensAdvizor reads
 *   the selected variant id from the form).
 * - Variant changes dispatch a bubbling `change` event on the product form
 *   (LensAdvizor app block hook) and push `?variant=` via replaceState.
 * - Add-to-cart POSTs to /cart/add.js, then dispatches `cart:refresh`
 *   with { open: true } so our <cart-drawer> re-renders and opens
 *   (live flow: modal cart opens on add).
 */

const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

export function formatMoney(cents, format = '${{amount}}') {
  function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? units + decimal + parts[1] : units;
  }

  let value = '';
  switch ((format.match(MONEY_PLACEHOLDER) || [])[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = withDelimiters(cents, 2);
  }
  return format.replace(MONEY_PLACEHOLDER, value);
}

const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.index = 0;

    this.buildDots();
    this.observeActiveSlide();

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goToIndex(i));
    });

    this.querySelectorAll('[data-gallery-nav]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.goToIndex(this.index + (btn.dataset.galleryNav === 'next' ? 1 : -1));
      });
    });

    this.setupLightbox();
    if (this.dataset.zoom === 'true') this.setupHoverZoom();
    this.setActive(0);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  goToMedia(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.goToIndex(index);
  }

  goToIndex(rawIndex) {
    const index = Math.max(0, Math.min(rawIndex, this.slides.length - 1));
    const offset = this.slides[index].offsetLeft - this.slides[0].offsetLeft;
    this.track.scrollTo({ left: offset, behavior: 'smooth' });
  }

  buildDots() {
    this.dotsWrap = this.querySelector('[data-gallery-dots]');
    if (!this.dotsWrap || this.slides.length < 2) return;
    const label = this.dotsWrap.dataset.dotLabel || 'Slide';
    this.dots = this.slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'carousel__dot';
      dot.setAttribute('aria-label', `${label} ${i + 1}`);
      dot.addEventListener('click', () => this.goToIndex(i));
      this.dotsWrap.appendChild(dot);
      return dot;
    });
  }

  observeActiveSlide() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  setActive(index) {
    this.index = index;
    // Eager-load neighbor slides so arrow navigation never waits on the network
    [index - 1, index + 1].forEach((i) => {
      const img = this.slides[i] && this.slides[i].querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  /* Click-to-open lightbox with prev/next and keyboard navigation across
     all media — replaces the live magnificPopup gallery popup. */
  setupLightbox() {
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    if (!this.lightbox) return;
    const image = this.lightbox.querySelector('img');
    if (!image) return;

    const openers = Array.from(this.querySelectorAll('[data-gallery-open]'));
    const items = openers.map((el) => ({ href: el.href, alt: el.dataset.alt || '' }));
    let index = 0;

    const show = (nextIndex) => {
      index = (nextIndex + items.length) % items.length;
      image.src = items[index].href;
      image.alt = items[index].alt;
      // Prefetch neighbors so arrow navigation feels instant
      [index + 1, index - 1].forEach((i) => {
        new Image().src = items[(i + items.length) % items.length].href;
      });
    };

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-gallery-open]');
      if (opener) {
        event.preventDefault();
        show(openers.indexOf(opener));
        this.lightbox.showModal();
        return;
      }
      const nav = event.target.closest('[data-lightbox-nav]');
      if (nav) {
        show(nav.dataset.lightboxNav === 'next' ? index + 1 : index - 1);
        return;
      }
      if (event.target.closest('[data-dialog-close]') || event.target === this.lightbox) {
        this.lightbox.close();
      }
    });

    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1);
      if (event.key === 'ArrowLeft') show(index - 1);
    });
  }

  /* Desktop hover zoom — replaces jquery.zoom (scale-in-place approximation). */
  setupHoverZoom() {
    if (!FINE_POINTER.matches) return;
    this.querySelectorAll('[data-gallery-zoom]').forEach((container) => {
      const img = container.querySelector('img');
      if (!img) return;
      container.addEventListener('pointermove', (event) => {
        const rect = container.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      });
      container.addEventListener('pointerenter', () => container.classList.add('is-zoomed'));
      container.addEventListener('pointerleave', () => {
        container.classList.remove('is-zoomed');
        img.style.transformOrigin = '';
      });
    });
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[name="quantity"]');
    if (!this.input) return;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-qty-change]');
      if (!button) return;
      const min = Number(this.input.min || 1);
      const max = Number(this.input.max || 999);
      const next = Number(this.input.value || min) + Number(button.dataset.qtyChange);
      this.input.value = Math.max(min, Math.min(max, next));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.input.addEventListener('change', () => {
      const min = Number(this.input.min || 1);
      const value = parseInt(this.input.value, 10);
      if (Number.isNaN(value) || value < min) this.input.value = min;
    });
  }
}

class VariantPicker extends HTMLElement {
  connectedCallback() {
    const jsonScript = this.querySelector('[data-product-json]');
    this.masterSelect = this.querySelector('select[name="id"]');
    if (!jsonScript || !this.masterSelect) return;
    this.product = JSON.parse(jsonScript.textContent);
    this.section = this.closest('[data-product-section]');
    this.form = this.closest('form');

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) this.onOptionChange();
    });
  }

  selectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('[data-option-index]'));
    return fieldsets
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);
  }

  onOptionChange() {
    const options = this.selectedOptions();
    const variant = this.product.variants.find((candidate) =>
      options.every((value, i) => value === null || candidate.options[i] === value)
    );
    this.updateVariant(variant);
  }

  updateVariant(variant) {
    const section = this.section || document;
    const addToCart = section.querySelector('[data-add-to-cart]');
    const addToCartText = section.querySelector('[data-add-to-cart-text]');
    const stock = section.querySelector('[data-stock-status]');

    if (!variant) {
      if (addToCart) {
        addToCart.disabled = true;
        if (addToCartText) addToCartText.textContent = this.dataset.textUnavailable;
      }
      if (stock) stock.textContent = '';
      return;
    }

    this.masterSelect.value = variant.id;

    // Price (+ compare-at) — .money spans, live #ProductPrice/#ComparePrice behaviour
    const price = section.querySelector('[data-price] .money');
    if (price) price.textContent = formatMoney(variant.price, this.dataset.moneyFormat);
    const compareItem = section.querySelector('[data-compare-price]');
    if (compareItem) {
      const compare = compareItem.querySelector('.money');
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareItem.classList.toggle('hidden', !onSale);
      if (compare && onSale) compare.textContent = formatMoney(variant.compare_at_price, this.dataset.moneyFormat);
    }

    // Stock status — live #variant-inventory contract
    if (stock) {
      stock.textContent = variant.available ? this.dataset.textStockIn : this.dataset.textStockOut;
      stock.classList.toggle('in-stock', variant.available);
      stock.classList.toggle('out-of-stock', !variant.available);
    }

    // Add-to-cart state
    if (addToCart) {
      addToCart.disabled = !variant.available;
      if (addToCartText) {
        addToCartText.textContent = variant.available ? this.dataset.textAddToCart : this.dataset.textSoldOut;
      }
    }

    // Gallery jump to the variant image
    if (variant.featured_media) {
      section.querySelector('media-gallery')?.goToMedia(variant.featured_media.id);
    }

    // Deep link (?variant=) — live data-enable-history-state="true"
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // App hook (LensAdvizor listens for form changes)
    this.form?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    event.preventDefault();
    const button = this.form.querySelector('[data-add-to-cart]');
    const error = this.querySelector('[data-form-error]');
    if (error) {
      error.textContent = '';
      error.classList.add('hidden');
    }
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    }

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.description || result.message || this.dataset.errorMessage);
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } catch (fetchError) {
      if (error) {
        error.textContent = fetchError.message || this.dataset.errorMessage;
        error.classList.remove('hidden');
      }
    } finally {
      if (button) {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  }
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
